import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PageHeader from '../PageHeader'
import ComercialChip from './ComercialChip'
import ComercialVacio from './ComercialVacio'
import { mensajeError, ordenarCatalogo, reordenar } from './comercialLogica'

// A Comercial catalogue (organization types, contact labels): create, rename,
// turn on and off, order with arrows (E4-5: works on a phone too) and delete
// one nobody uses -- one in use is turned off instead (the server says so).
//   api: { listar, crear, actualizar, eliminar }
//   textos: the comercial.json key with this catalogue's texts ('tipos' | 'etiquetas')
//   puede: may edit (comercial_configurar); without it the list is read-only
export default function ComercialCatalogo({ api, textos, puede }) {
  const { t } = useTranslation('comercial')
  const tx = (k, o) => t(`${textos}.${k}`, o)
  const [items, setItems] = useState(null)
  const [nuevo, setNuevo] = useState('')
  const [editando, setEditando] = useState(null)      // { id, nombre }
  const [ocupado, setOcupado] = useState(false)
  const [error, setError] = useState('')

  const fallo = useCallback((e) => setError(mensajeError(e, t, t('catalogo.fallo'))), [t])
  const cargar = useCallback(() => api.listar().then(l => setItems(ordenarCatalogo(l))).catch(fallo), [api, fallo])
  useEffect(() => { cargar() }, [cargar])

  // Every change goes to the server and the list is reloaded from it.
  const hacer = async (fn) => {
    setOcupado(true)
    setError('')
    try { await fn(); return true }
    catch (e) { fallo(e); return false }
    finally { await cargar(); setOcupado(false) }
  }

  const crear = async (e) => {
    e.preventDefault()
    const nombre = nuevo.trim()
    if (!nombre) return
    if (await hacer(() => api.crear({ nombre }))) setNuevo('')
  }
  const renombrar = async (e) => {
    e.preventDefault()
    const nombre = editando.nombre.trim()
    const actual = items.find(x => x.id === editando.id)
    if (!nombre || nombre === actual?.nombre) { setEditando(null); return }
    if (await hacer(() => api.actualizar(editando.id, { nombre }))) setEditando(null)
  }
  const mover = (id, dir) => {
    const cambios = reordenar(items, id, dir)
    if (cambios.length) hacer(async () => { for (const c of cambios) await api.actualizar(c.id, { orden: c.orden }) })
  }
  const alternar = (x) => hacer(() => api.actualizar(x.id, { activo: !x.activo }))
  const eliminar = (x) => {
    if (!window.confirm(tx('eliminarConfirm', { nombre: x.nombre }))) return
    hacer(() => api.eliminar(x.id))
  }

  const boton = 'w-7 h-7 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-30 disabled:hover:bg-transparent'

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-6">
        <PageHeader title={tx('titulo')} subtitle={tx('subtitulo')} />
      </div>
      <div className="max-w-3xl">
        {error && <p role="alert" className="mb-3 text-sm text-red-600">{error}</p>}
        {puede && (
          <form onSubmit={crear} className="mb-4 flex gap-2">
            <input value={nuevo} onChange={e => setNuevo(e.target.value)} placeholder={tx('nuevoPlaceholder')} maxLength={100}
              className="flex-1 min-w-0 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200" />
            <button type="submit" disabled={ocupado || !nuevo.trim()}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40 shrink-0">
              {t('catalogo.agregar')}
            </button>
          </form>
        )}
        {!items ? <p className="text-sm text-gray-400">{t('catalogo.cargando')}</p>
          : !items.length ? <ComercialVacio titulo={tx('vacio')} />
          : (
            <ul className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
              {items.map((x, i) => (
                <li key={x.id} className="px-3 sm:px-4 py-2.5 flex items-center gap-2 sm:gap-3">
                  {puede && (
                    <div className="flex flex-col sm:flex-row shrink-0">
                      <button type="button" className={boton} disabled={ocupado || i === 0} onClick={() => mover(x.id, -1)}
                        title={t('catalogo.subir')} aria-label={t('catalogo.subir')}>↑</button>
                      <button type="button" className={boton} disabled={ocupado || i === items.length - 1} onClick={() => mover(x.id, +1)}
                        title={t('catalogo.bajar')} aria-label={t('catalogo.bajar')}>↓</button>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    {editando?.id === x.id ? (
                      <form onSubmit={renombrar} className="flex gap-2">
                        <input autoFocus value={editando.nombre} maxLength={100}
                          onChange={e => setEditando({ ...editando, nombre: e.target.value })}
                          onKeyDown={e => { if (e.key === 'Escape') setEditando(null) }}
                          className="flex-1 min-w-0 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200" />
                        <button type="submit" disabled={ocupado} className="text-sm text-gray-900 font-medium hover:underline">{t('catalogo.guardar')}</button>
                        <button type="button" onClick={() => setEditando(null)} className="text-sm text-gray-500 hover:underline">{t('catalogo.cancelar')}</button>
                      </form>
                    ) : (
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className={`text-sm font-medium ${x.activo ? 'text-gray-900' : 'text-gray-400'}`}>{x.nombre}</span>
                        {!x.activo && <ComercialChip variante="inactivo">{t('chips.inactivo')}</ComercialChip>}
                        <span className="text-xs text-gray-400">{tx('enUso', { count: x.en_uso })}</span>
                      </div>
                    )}
                  </div>
                  {puede && editando?.id !== x.id && (
                    <div className="flex flex-wrap justify-end gap-x-3 gap-y-1 shrink-0 text-xs">
                      <button type="button" disabled={ocupado} onClick={() => setEditando({ id: x.id, nombre: x.nombre })}
                        className="text-gray-600 hover:text-gray-900 hover:underline">{t('catalogo.renombrar')}</button>
                      <button type="button" disabled={ocupado} onClick={() => alternar(x)}
                        className="text-gray-600 hover:text-gray-900 hover:underline">{x.activo ? t('catalogo.desactivar') : t('catalogo.activar')}</button>
                      <button type="button" disabled={ocupado} onClick={() => eliminar(x)}
                        title={x.en_uso ? tx('eliminarEnUso') : undefined}
                        className="text-red-600 hover:text-red-800 hover:underline">{t('catalogo.eliminar')}</button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        <p className="text-xs text-gray-400 mt-3">{tx('nota')}</p>
      </div>
    </div>
  )
}
